'use client';

import type { INavListItem } from '@/api/types/public';
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import LensProcessNavPc from './LensProcessNav.pc';
import {
  RESPONSIVE_RESOURCE_SLOT_SIZE,
  resolveTextLensResourceVisibility,
  type TextLensResourceVisibility,
  WIDE_LENS_RESOURCE_SLOT_SIZE,
} from './navigationLayoutPriority';
import ResourceSlotNav from './ResourceSlotNav.pc';
import styles from './index.module.scss';
import TextNav from './TextNav';

interface TextLensResourceNavigationLayoutProps {
  textNavItems: INavListItem[];
  lensProcessItems: INavListItem[];
  resourceSlotItems: INavListItem[];
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  isActive: boolean;
}

const RESPONSIVE_DESKTOP_QUERY = '(min-width: 1281px) and (max-width: 1919px)';
const WIDE_DESKTOP_QUERY = '(min-width: 1920px)';

const TextLensResourceNavigationLayout = ({
  textNavItems,
  lensProcessItems,
  resourceSlotItems,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  isActive,
}: TextLensResourceNavigationLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const textNavigationRef = useRef<HTMLDivElement>(null);
  const lensProcessItem = lensProcessItems[0];
  const textNavItemCount = textNavItems.length;
  const resourceSlotCount = resourceSlotItems.length;
  const hasLensProcess = Boolean(lensProcessItem);
  const [visibility, setVisibility] = useState<
    TextLensResourceVisibility & { resourceSlotSize: number }
  >({
    showLensProcess: hasLensProcess,
    visibleResourceSlotCount: resourceSlotCount,
    resourceSlotSize: RESPONSIVE_RESOURCE_SLOT_SIZE,
  });

  useLayoutEffect(() => {
    if (!isActive) return;

    const updateModuleVisibility = () => {
      const layout = layoutRef.current;
      const textNavigation = textNavigationRef.current;
      if (!layout || !textNavigation) return;

      const isResponsiveDesktop = window.matchMedia(RESPONSIVE_DESKTOP_QUERY).matches;
      const isWideDesktop = window.matchMedia(WIDE_DESKTOP_QUERY).matches;
      const shouldResolveVisibility = isResponsiveDesktop || isWideDesktop;
      const minimumResourceSlotSize =
        isWideDesktop && hasLensProcess
          ? WIDE_LENS_RESOURCE_SLOT_SIZE
          : RESPONSIVE_RESOURCE_SLOT_SIZE;
      const resourceSlotSize = Math.max(minimumResourceSlotSize, layout.clientHeight);
      const nextVisibility = {
        ...(shouldResolveVisibility
          ? resolveTextLensResourceVisibility({
              availableWidth: layout.clientWidth,
              textNavigationWidth: textNavigation.scrollWidth,
              hasLensProcess,
              resourceSlotCount,
              resourceSlotSize,
            })
          : {
              showLensProcess: hasLensProcess,
              visibleResourceSlotCount: resourceSlotCount,
            }),
        resourceSlotSize,
      };

      setVisibility((currentVisibility) =>
        currentVisibility.showLensProcess === nextVisibility.showLensProcess &&
        currentVisibility.visibleResourceSlotCount === nextVisibility.visibleResourceSlotCount &&
        currentVisibility.resourceSlotSize === nextVisibility.resourceSlotSize
          ? currentVisibility
          : nextVisibility,
      );
    };

    updateModuleVisibility();

    const resizeObserver = new ResizeObserver(updateModuleVisibility);
    if (layoutRef.current) resizeObserver.observe(layoutRef.current);
    if (textNavigationRef.current) resizeObserver.observe(textNavigationRef.current);

    return () => resizeObserver.disconnect();
  }, [hasLensProcess, isActive, resourceSlotCount, textNavItemCount]);

  const visibleResourceSlotItems = resourceSlotItems.slice(0, visibility.visibleResourceSlotCount);
  const hasVisibleRightModule = visibility.showLensProcess || visibleResourceSlotItems.length > 0;

  return (
    <div ref={layoutRef} className={styles.textLensResourceNavigationLayout}>
      <div
        ref={textNavigationRef}
        className={`flex items-start ${styles.textLensResourceNavigationTextContainer}`}
      >
        {textNavItems.map((item, index) => (
          <div
            key={`${item.id}-${item.template_id}`}
            className={styles.responsiveTextNavigationColumn}
          >
            <TextNav
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              secondLevelIndex={index}
              isLast={index === textNavItems.length - 1}
              isDarkMode={isDarkMode}
              isActive={isActive}
            />
          </div>
        ))}
      </div>

      {hasVisibleRightModule ? (
        <div
          className={`ml-auto flex shrink-0 items-stretch ${styles.lensProcessContainer} ${styles.textLensResourceRightContainer}`}
        >
          {visibility.showLensProcess && lensProcessItem ? (
            <LensProcessNavPc isDarkMode={isDarkMode} />
          ) : null}

          {visibleResourceSlotItems.length > 0 ? (
            <div
              className={`flex shrink-0 ${styles.textLensResourceResourceContainer}`}
              style={
                {
                  '--resource-slot-size': `${visibility.resourceSlotSize}px`,
                } as CSSProperties
              }
            >
              {visibleResourceSlotItems.map((item, resourceIndex) => (
                <ResourceSlotNav
                  key={`${item.id}-${item.template_id}`}
                  item={item}
                  firstLevelIndex={firstLevelIndex}
                  firstLevelTitle={firstLevelTitle}
                  resourceIndex={resourceIndex}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TextLensResourceNavigationLayout;
